import type { TFunction } from 'i18next';
import { z } from 'zod';

import { AddressSchema as GeneratedAddressSchema } from '@/core/graphql/generated/validation.zod';
import { phoneNumberValidator } from '@/utils/validators/zod/phoneNumberValidator';

const cleanString = (v: string | null | undefined) => (v ?? '').trim();

export const AddressSchema = (t: TFunction) => {
	const raw = GeneratedAddressSchema().pick({
		buildingIdentifier_V: true,
		businessCda_V: true,
		cbsa_V: true,
		cityCda_V: true,
		countryCda_V: true,
		dpvConfirmedIndicator_V: true,
		faxCda_V: true,
		latitudeCda_V: true,
		longitudeCda_V: true,
		phoneCda_V: true,
		postalCodeCda_V: true,
		primaryCda_V: true,
		stateCda_V: true,
		statusCda_V: true,
		streetAddress1Cda_V: true,
		streetAddress2Cda_V: true,
		vid_V: true
	});

	const requiredMsg = t
		? t('This field is required')
		: 'This field is required';

	return z
		.object({
			streetAddress1Cda_V: z.string().nullish().transform(cleanString),
			streetAddress2Cda_V: z.string().nullish().transform(cleanString),
			cityCda_V: z.string().nullish().transform(cleanString),
			stateCda_V: z.string().nullish().transform(cleanString),
			postalCodeCda_V: z.string().nullish().transform(cleanString),
			postalCodeFullUs_V: z.string().nullish().transform(cleanString),

			countryCda_V: z.string().nullish().transform(cleanString),
			subStateAdminUnit_V: z.string().nullish().transform(cleanString),

			phoneCda_V: phoneNumberValidator(z.string().nullish()),
			// Note(Filip): As of 27.01.2026 - Jonas reqested these to be hidden for the time being
			// cbsa_V: z.string().nullish().transform(cleanString),

			primaryCda_V: raw.shape.primaryCda_V
				.nullish()
				.transform(v => (v ? true : false)),
			businessCda_V: raw.shape.businessCda_V
				.nullish()
				.transform(v => (v ? true : false)),

			vid_V: z.string().nullish().transform(cleanString),
			statusCda_V: z.string().nullish(),
			_origEmptyStreet: z.boolean().nullish(),
			_origEmptyCity: z.boolean().nullish(),
			buildingIdentifier_V: raw.shape.buildingIdentifier_V.nullish(),
			dpvConfirmedIndicator_V: raw.shape.dpvConfirmedIndicator_V.nullish(),
			latitudeCda_V: raw.shape.latitudeCda_V.nullish(),
			longitudeCda_V: raw.shape.longitudeCda_V.nullish(),
			faxCda_V: raw.shape.faxCda_V.nullish()
		})
		.superRefine((data, ctx) => {
			const isNew = !data.vid_V;
			const isInactive = data.statusCda_V !== 'Active';
			const canSkip = !isNew && isInactive;

			const skipStreet = canSkip && !!data._origEmptyStreet;
			const skipCity = canSkip && !!data._origEmptyCity;

			if (!skipStreet && !data.streetAddress1Cda_V) {
				ctx.addIssue({
					code: 'custom',
					message: requiredMsg,
					path: ['streetAddress1Cda_V']
				});
			}
			if (!skipCity && !data.cityCda_V) {
				ctx.addIssue({
					code: 'custom',
					message: requiredMsg,
					path: ['cityCda_V']
				});
			}
		});
};

export const AddressSectionSchema = (t: TFunction) => {
	return z.object({
		addresses: z.array(AddressSchema(t))
	});
};

export type AddressValuesIn = z.input<ReturnType<typeof AddressSchema>>;
export type AddressSectionValuesIn = z.input<
	ReturnType<typeof AddressSectionSchema>
>;
